// Connection-quality monitor built on RTCPeerConnection.getStats().
// Polls round-trip time + packet loss and maps them to good/medium/poor.

export type QualityLevel = "good" | "medium" | "poor";

export interface Quality {
  level: QualityLevel;
  rttMs?: number;
  lossPct?: number;
  relayed: boolean;
}

export interface QualityMonitor {
  stop(): void;
}

export function monitorQuality(
  pc: RTCPeerConnection,
  onChange: (q: Quality) => void,
  intervalMs = 2000
): QualityMonitor {
  let prevLost = 0;
  let prevReceived = 0;
  let relayed = false;

  const tick = async () => {
    let stats: RTCStatsReport;
    try {
      stats = await pc.getStats();
    } catch {
      return;
    }

    let rttMs: number | undefined;
    let lost = 0;
    let received = 0;

    stats.forEach((report) => {
      if (
        report.type === "candidate-pair" &&
        (report as RTCIceCandidatePairStats).nominated &&
        (report as RTCIceCandidatePairStats).state === "succeeded"
      ) {
        const rtt = (report as RTCIceCandidatePairStats).currentRoundTripTime;
        if (typeof rtt === "number") rttMs = Math.round(rtt * 1000);
        const localId = (report as RTCIceCandidatePairStats).localCandidateId;
        const local =
          localId &&
          (stats.get(localId) as { candidateType?: string } | undefined);
        if (local && local.candidateType === "relay") relayed = true;
      }
      if (report.type === "inbound-rtp") {
        const r = report as RTCInboundRtpStreamStats;
        lost += r.packetsLost ?? 0;
        received += r.packetsReceived ?? 0;
      }
    });

    const dLost = Math.max(0, lost - prevLost);
    const dRecv = Math.max(0, received - prevReceived);
    prevLost = lost;
    prevReceived = received;
    const lossPct =
      dLost + dRecv > 0 ? (dLost / (dLost + dRecv)) * 100 : undefined;

    onChange({ level: classify(rttMs, lossPct), rttMs, lossPct, relayed });
  };

  void tick();
  const id = window.setInterval(() => void tick(), intervalMs);
  return { stop: () => window.clearInterval(id) };
}

function classify(rttMs?: number, lossPct?: number): QualityLevel {
  const rtt = rttMs ?? 0;
  const loss = lossPct ?? 0;
  if (rtt < 150 && loss < 2) return "good";
  if (rtt < 400 && loss < 7) return "medium";
  return "poor";
}
